import { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import EVTool from './pages/EVTool';
import BetTracker from './pages/BetTracker';
import Odds from './pages/Odds';
import Props from './pages/Props';
import Pickem from './pages/Pickem';
import Arbitrage from './pages/Arbitrage';
import Calculators from './pages/Calculators';
import Results from './pages/Results';
import Projections from './pages/Projections';
import { Box, Typography, IconButton, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import logo from './assets/4f4logo.png';
import logo2 from './assets/sharpstack2.png';
import { checkUserAuth, forceRefreshIfExpired } from './auth';
import logger from './logger';
import Bugs from './pages/Bugs';
import ChangeLog from './pages/ChangeLog'; 
import Middles from './pages/Middles';

// WebSocket endpoint
const WS_URL = 'wss://ws.betpros.ai:8080';

// Define pages that don't require authentication
const PUBLIC_PAGES = ['/odds', '/props', '/calculators', '/bugs', '/changelog', '/projections', '/results'];

// Protected Page Component
function ProtectedPage({ isAuthenticated, authError, authReason, children }) {
  // Redirect to plans page if user doesn't have betting plan
  if (!isAuthenticated && authReason === 'no-betting-plan') {
    window.location.href = 'https://www.4for4.com/plans';
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212',
          color: '#ffffff',
        }}
      >
        <Typography variant="h6">Redirecting to upgrade your plan...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212',
          color: '#ffffff',
        }}
      >
        <Typography variant="h6">{authError || 'Please log in to access this page.'}</Typography>
      </Box>
    );
  }
  return children;
}

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const [updatesPaused, setUpdatesPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Connecting');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;
  const isConnectingRef = useRef(false);
  const hasStoredUsername = useRef(false);
  const bettingMetricsLoaded = useRef(false);
  const nflProjectionsLoaded = useRef(false);
  const nbaProjectionsLoaded = useRef(false);
  const trackedBetsRequested = useRef(false);

  const [arbitrageData, setArbitrageData] = useState([]);
  const betsDataRef = useRef(new Map());
  const [betsDataVersion, forceUpdateBets] = useReducer(x => x + 1, 0);
  const [trackedBets, setTrackedBets] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authReason, setAuthReason] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [closingOdds, setClosingOdds] = useState([]);
  const [bettingMetrics, setBettingMetrics] = useState([]);
  const [nflProjections, setNflProjections] = useState([]);
  const [nbaProjections, setNbaProjections] = useState([]);
  const [middleData, setMiddleData] = useState([]);

  // Add state and ref for dynamic header height
  const [connectionPanelHeight, setConnectionPanelHeight] = useState(48);
  const connectionPanelRef = useRef(null);

  // Check if current page requires authentication
  const isPublicPage = PUBLIC_PAGES.includes(location.pathname);
  const shouldConnectWebSocket = isAuthenticated || isPublicPage || location.pathname === '/evtool';

  // Add useEffect to measure and update the connection panel height
  useEffect(() => {
    const updateHeight = () => {
      if (connectionPanelRef.current) {
        const height = connectionPanelRef.current.getBoundingClientRect().height;
        setConnectionPanelHeight(height);
        document.documentElement.style.setProperty('--connection-panel-height', `${height}px`);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    const timeoutId = setTimeout(updateHeight, 100);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId);
    };
  }, [userName, isMobile]);

  useEffect(() => {
    const updateFilterHeight = () => {
      const filterEl = document.querySelector('.filter-row');
      if (!filterEl) return;
      const h = Math.round(filterEl.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--filter-section-height', `${h}px`);
    };
    updateFilterHeight();
    window.addEventListener('resize', updateFilterHeight);
    return () => window.removeEventListener('resize', updateFilterHeight);
  }, []);

  // Track page changes
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isAuthenticated && userName) {
      wsRef.current.send(JSON.stringify({
        action: 'update_page',
        page: location.pathname,
        userName: userName
      }));
      logger.log('Sent page update:', location.pathname);
    }
  }, [location.pathname, isAuthenticated, userName, connectionStatus]);

  // Fetch betting metrics when navigating to Results page
  useEffect(() => {
    if (
      location.pathname === '/results' &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      bettingMetrics.length === 0
    ) {
      logger.log('Fetching betting metrics for Results page');
      wsRef.current.send(JSON.stringify({ action: 'get_betting_metrics' }));
    }
  }, [location.pathname, connectionStatus, bettingMetrics.length]);

  // Fetch NFL projections when navigating to Projections page
  useEffect(() => {
    if (
      location.pathname === '/projections' &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      nflProjections.length === 0
    ) {
      logger.log('Fetching NFL projections for Projections page');
      wsRef.current.send(JSON.stringify({ action: 'get_nfl_projections' }));
      nflProjectionsLoaded.current = true;
    }
  }, [location.pathname, connectionStatus, nflProjections.length]);

  // Fetch NBA projections when navigating to Projections page
  useEffect(() => {
    if (
      location.pathname === '/projections' &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      nbaProjections.length === 0
    ) {
      logger.log('Fetching NBA projections for Projections page');
      wsRef.current.send(JSON.stringify({ action: 'get_nba_projections' }));
      nbaProjectionsLoaded.current = true;
    }
  }, [location.pathname, connectionStatus, nbaProjections.length]);

  useEffect(() => {
    const fetchTrackedBets = () => {
      if (
        isAuthenticated &&
        userName &&
        userName !== 'anonymous' &&
        wsRef.current &&
        wsRef.current.readyState === WebSocket.OPEN &&
        !trackedBetsRequested.current
      ) {
        logger.log('Fetching tracked bets for user:', userName);
        wsRef.current.send(JSON.stringify({ action: 'get_tracked_bets', userName, timestamp: Date.now() }));
        trackedBetsRequested.current = true;
        logger.log('Sent get_tracked_bets message:', userName);
      }
    };

    fetchTrackedBets();
  }, [isAuthenticated, userName, connectionStatus]);

  const trackBet = useCallback((bet) => {
    if (!isAuthenticated) {
      logger.log('Cannot track bet - user not authenticated');
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'track_bet',
        userName,
        bet,
      }));
      logger.log('Sent track_bet message:', { userName, bet });
    } else {
      logger.error('WebSocket not connected, cannot track bet:', { userName, bet });
    }
  }, [isAuthenticated, userName]);

  const untrackBet = useCallback((oddsId) => {
    if (!isAuthenticated) {
      logger.log('Cannot untrack bet - user not authenticated');
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'untrack_bet',
        userName,
        oddsId,
      }));
      logger.log('Sent untrack_bet message:', { userName, oddsId });
    } else {
      logger.error('WebSocket not connected, cannot untrack bet:', { userName, oddsId });
    }
  }, [isAuthenticated, userName]);

  const connectWebSocket = (authName, authPlan) => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      logger.log('WebSocket already open or connecting, readyState:', wsRef.current.readyState);
      return;
    }
    if (isConnectingRef.current) {
      logger.log('WebSocket connection attempt already in progress');
      return;
    }

    logger.log('Initiating WebSocket connection to:', WS_URL);
    logger.log('Auth values passed to connectWebSocket - name:', authName, 'plan:', authPlan);
    isConnectingRef.current = true;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    setConnectionStatus('Connecting');

    ws.onopen = () => {
      logger.log('WebSocket connected, readyState:', ws.readyState);

      // Include authenticated flag in subscriptions
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'arbitrage',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'bets',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      ws.send(JSON.stringify({
        action: 'subscribe',
        type: 'middles',
        authenticated: isAuthenticated,
        timestamp: Date.now()
      }));
      logger.log('Sent subscription messages with authenticated flag:', isAuthenticated);
      setConnectionStatus(updatesPaused ? 'Paused' : 'Connected');
      setError(null);
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;

      if (!bettingMetricsLoaded.current && isAuthenticated) {
        ws.send(JSON.stringify({ action: 'get_betting_metrics', timestamp: Date.now() }));
        bettingMetricsLoaded.current = true;
        logger.log('Sent initial get_betting_metrics message');
      }

      // Fetch projections if on projections page (works for public users too)
      if (location.pathname === '/projections') {
        if (!nflProjectionsLoaded.current) {
          ws.send(JSON.stringify({ action: 'get_nfl_projections' }));
          nflProjectionsLoaded.current = true;
          logger.log('Fetching NFL projections on WebSocket connection');
        }
        if (!nbaProjectionsLoaded.current) {
          ws.send(JSON.stringify({ action: 'get_nba_projections' }));
          nbaProjectionsLoaded.current = true;
          logger.log('Fetching NBA projections on WebSocket connection');
        }
      }

      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
          logger.log('Sent ping message');
        }
      }, 25000);

      // Use the auth values passed to this function
      const nameToUse = authName || userName;
      const planToUse = authPlan || userPlan;

      logger.log('Using auth values - name:', nameToUse, 'plan:', planToUse);
      logger.log('hasStoredUsername.current:', hasStoredUsername.current);

      // Check if we have a valid authenticated user
      if (nameToUse && nameToUse !== 'anonymous') {
        // Send auth message
        ws.send(JSON.stringify({ action: 'auth', name: nameToUse }));
        logger.log('Sent auth message with userName:', nameToUse);

        // Store username with plan
        if (!hasStoredUsername.current) {
          const storeMessage = {
            action: 'store_username',
            username: nameToUse,
            plan: planToUse
          };
          logger.log('About to send store_username message:', JSON.stringify(storeMessage));
          ws.send(JSON.stringify(storeMessage));
          logger.log('Sent store_username message:', nameToUse, 'with plan:', planToUse);
          hasStoredUsername.current = true;
        } else {
          logger.log('Skipping store_username because hasStoredUsername.current is true');
        }

        // Send initial page update
        if (location.pathname) {
          ws.send(JSON.stringify({
            action: 'update_page',
            page: location.pathname,
            userName: nameToUse
          }));
          logger.log('Sent initial page update:', location.pathname);
        }

        // Fetch tracked bets
        if (!trackedBetsRequested.current) {
          ws.send(JSON.stringify({ action: 'get_tracked_bets', userName: nameToUse, timestamp: Date.now() }));
          trackedBetsRequested.current = true;
          logger.log('Sent get_tracked_bets message:', nameToUse);
        }
      } else {
        logger.log('Not sending store_username - invalid user or anonymous');
        // Still send page update for anonymous users
        if (location.pathname) {
          ws.send(JSON.stringify({
            action: 'update_page',
            page: location.pathname,
            userName: 'anonymous'
          }));
        }
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (updatesPaused && data.type !== 'store_username_response') {
          logger.log('Updates paused, ignoring message');
          return;
        }

        if (data.type === 'store_username_response') {
          if (data.status === 'success') {
            logger.log(`Username stored successfully, userId: ${data.userId}, plan: ${data.plan}`);
          } else {
            logger.error(`Failed to store username: ${data.message}`);
          }
        } else if (data.type === 'track_bet_response') {
          if (data.status === 'success') {
            logger.log(`Successfully tracked bet with oddsId: ${data.oddsId}`);
            if (isAuthenticated && userName && userName !== 'anonymous' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'get_tracked_bets', userName }));
              logger.log('Refreshing tracked bets after successful track');
            }
          } else {
            logger.error(`Failed to track bet: ${data.message}`);
          }
        } else if (data.type === 'untrack_bet_response') {
          if (data.status === 'success') {
            logger.log(`Successfully untracked bet with oddsId: ${data.oddsId}`);
            if (isAuthenticated && userName && userName !== 'anonymous' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'get_tracked_bets', userName }));
              logger.log('Refreshing tracked bets after successful untrack');
            }
          } else {
            logger.error(`Failed to untrack bet: ${data.message}`);
          }
        } else if (data.type === 'betting_metrics_response') {
          if (data.status === 'success') {
            setBettingMetrics(data.metrics);
            logger.log('Received betting metrics:', data.metrics.length, 'records');
            logger.log('Metrics last updated:', data.lastUpdated);
          } else {
            logger.error(`Failed to fetch betting metrics: ${data.message}`);
          }
        } else if (data.type === 'nfl_projections_response' && data.status === 'success') {
          setNflProjections(data.projections);
          logger.log('Received NFL projections:', data.projections.length, 'records');
        } else if (data.type === 'nba_projections_response' && data.status === 'success') {
          setNbaProjections(data.projections);
          logger.log('Received NBA projections:', data.projections.length, 'records');
        } else if (data.type === 'tracked_bets' && data.bets) {
          const processedTrackedBets = data.bets.map((bet, index) => ({
            ...bet,
            id: bet.oddsId ?? index + 1,
          }));
          setTrackedBets(processedTrackedBets);
        } else if (data.type === 'arbitrage' && data.arbitrages) {
          const processedArbs = data.arbitrages.map((item, index) => ({
            ...item,
            id: item.id ?? index + 1,
          }));
          setArbitrageData(processedArbs);
          setLastUpdate(new Date());
          setError(null);
        } else if (data.type === 'arbitrage_delta' && data.arbitrages) {
          const processedDeltaArbs = data.arbitrages.map((item, index) => ({
            ...item,
            id: item.id ?? index + 1,
          }));
          setArbitrageData(prevArbs => {
            const arbMap = new Map(prevArbs.map(arb => [arb.id, arb]));
            for (const arb of processedDeltaArbs) {
              arbMap.set(arb.id, arb);
            }
            const updatedArbs = Array.from(arbMap.values());
            logger.log('Processed arbitrage delta:', processedDeltaArbs.length, 'items, total:', updatedArbs.length);
            return updatedArbs;
          });
          setLastUpdate(new Date());
          setError(null);
        } else if (data.type === 'middles' && data.middles) {
          const processedMiddles = data.middles.map((item, index) => ({
            ...item,
            id: item.oddsId1 ?? index + 1,
          }));
          setMiddleData(processedMiddles);
          setLastUpdate(new Date());
          setError(null);
} else if (data.type === 'bets' && data.bets) {
  betsDataRef.current.clear();
  
  for (const item of data.bets) {
    const id = item.id ?? Date.now();
    if (!item.id) item.id = id;
    betsDataRef.current.set(id, item);
  }
          
          forceUpdateBets();
          setLastUpdate(new Date());
          setError(null);
          logger.log('Initial bets loaded:', betsDataRef.current.size, 'items');
        } else if (data.type === 'bets_delta' && data.bets) {
  for (const item of data.bets) {
    const id = item.id ?? Date.now();
    
    if (item.deleted) {
      betsDataRef.current.delete(id);
    } else {
      // Mutate instead of spread
      if (!item.id) item.id = id;
      betsDataRef.current.set(id, item);
    }
  }
          
          forceUpdateBets();
          setLastUpdate(new Date());
          setError(null);
          logger.log('Processed bets delta:', data.bets.length, 'items, total:', betsDataRef.current.size);
        } else if (data.type === 'closing_odds_response' && data.status === 'success') {
          setClosingOdds(data.closingOdds);
          logger.log('Received closing odds:', data.closingOdds);
        } else if (data.type === 'closing_odds_response' && data.status === 'error') {
          logger.error(`Failed to fetch closing odds: ${data.message}`);
        } else if (data.type === 'nfl_projections_update') {
          setNflProjections(data.projections);
          logger.log('Received NFL projections update:', data.projections.length, 'records');
        } else if (data.type === 'nba_projections_update') {
          setNbaProjections(data.projections);
          logger.log('Received NBA projections update:', data.projections.length, 'records');
        } else {
          logger.warn('Unhandled message type:', data.type);
        }
      } catch (err) {
        logger.error('Error parsing message:', err, 'Data:', event.data);
        setError('Failed to process server data. Please try refreshing.');
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error:', error, 'readyState:', ws.readyState);
      setConnectionStatus('Disconnected');
      setError('WebSocket connection failed. Retrying...');
      isConnectingRef.current = false;
    };

    ws.onclose = (event) => {
      logger.log('WebSocket closed, readyState:', ws.readyState, 'Code:', event.code, 'Reason:', event.reason);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      bettingMetricsLoaded.current = false;
      nflProjectionsLoaded.current = false;
      nbaProjectionsLoaded.current = false;
      trackedBetsRequested.current = false;
      hasStoredUsername.current = false;

      if (updatesPaused) {
        setConnectionStatus('Paused');
        setError(null);
        isConnectingRef.current = false;
      } else if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
        logger.log(`Reconnecting attempt ${reconnectAttemptsRef.current + 1} in ${delay}ms`);
        setConnectionStatus('Connecting');
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket(authName, authPlan);
        }, delay);
      } else {
        setConnectionStatus('Disconnected');
        setError('Failed to connect to data server. Please refresh the page.');
        isConnectingRef.current = false;
      }
    };
  };

  // Initial setup for authentication and interval
  useEffect(() => {
    logger.log('Performing initial authentication check');

    const { isValid, name, reason, plan } = checkUserAuth();
    setIsAuthenticated(isValid);
    setUserName(name);
    setAuthReason(reason);
    setUserPlan(plan);

    if (!isValid) {
      if (reason === 'no-betting-plan') {
        setAuthError('You need a betting plan to access this feature. Redirecting to upgrade...');
      } else if (reason === 'no-params') {
        setAuthError(null);
      } else {
        setAuthError('Please log in to access the app.');
      }
    } else {
      setAuthError(null);
    }

    logger.log('Initial authentication status:', { isValid, name, reason, plan });

    const urlParams = new URLSearchParams(window.location.search);
    const timestamp = urlParams.get('timestamp');
    let interval = null;
    if (timestamp && isValid) {
      forceRefreshIfExpired(timestamp);
      interval = setInterval(() => {
        forceRefreshIfExpired(timestamp);
      }, 60 * 60 * 1000);
    }

    return () => {
      logger.log('Cleaning up WebSocket and interval');
      if (wsRef.current && (wsRef.current.readyState !== WebSocket.CLOSED && wsRef.current.readyState !== WebSocket.CLOSING)) {
        wsRef.current.close(1000, 'Normal closure');
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (interval) {
        clearInterval(interval);
      }
      isConnectingRef.current = false;
      trackedBetsRequested.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated && authReason === 'no-betting-plan' && !isPublicPage && location.pathname !== '/evtool') {
      logger.log('Redirecting user without betting plan to upgrade page');
      window.top.location.href = 'https://www.4for4.com/plans';
    }
  }, [isAuthenticated, authReason, isPublicPage, location.pathname]);

  useEffect(() => {
    if (shouldConnectWebSocket && userName !== null && userPlan !== null) {
      logger.log('Triggering WebSocket connection. Authenticated:', isAuthenticated, 'Public page:', isPublicPage);
      logger.log('Passing to connectWebSocket - userName:', userName, 'userPlan:', userPlan);
      connectWebSocket(userName, userPlan);
    }
  }, [shouldConnectWebSocket, userName, userPlan]);

  const handlePauseToggle = () => {
    setUpdatesPaused(prev => {
      const newState = !prev;
      setConnectionStatus(newState ? 'Paused' : wsRef.current && wsRef.current.readyState === WebSocket.OPEN ? 'Connected' : 'Connecting');
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: newState ? 'pause' : 'resume' }));
      }
      if (newState && reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      } else if (!newState && wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
        connectWebSocket(userName, userPlan);
      }
      logger.log('Updates paused state changed:', newState);
      return newState;
    });
  };

  const formatLastUpdate = () => {
    const label = 'Last Update:';
    if (!lastUpdate) return { label, timestamp: '' };

    const opts = {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };

    const dateString = lastUpdate.toLocaleString('en-US', opts);
    const parts = dateString.split(', ');

    if (parts.length === 2) {
      const [mdy, t] = parts;
      return { label, timestamp: `${mdy} ${t.toLowerCase()}` };
    } else {
      return { label, timestamp: dateString };
    }
  };

  const getStatusTooltip = () => {
    switch (connectionStatus) {
      case 'Connected':
        return 'Connected to Database';
      case 'Connecting':
        return 'Reconnecting';
      case 'Paused':
      case 'Disconnected':
        return 'Disconnected from Database';
      default:
        return 'Unknown Status';
    }
  };

  // Convert Map to array for child components
  const betsData = useMemo(() => 
    Array.from(betsDataRef.current.values()), 
    [betsDataVersion]
  );

  return (
    <div className="app-container">
      <Box
        ref={connectionPanelRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#121212',
          zIndex: 1200,
          padding: '0px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #444',
        }}
        className="connection-panel"
      >
        {/* Left side - Logo (always on left) */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={logo2}
            alt="4for4 Logo"
            style={{
              width: '100px',
              height: 'auto',
              marginLeft: '0px',
            }}
          />
          {/* Desktop username (next to logo) */}
          {userName && !isMobile && (
            <Typography sx={{ color: '#ffffff', ml: 2 , mt: .6}}>
              Welcome, {userName}
            </Typography>
          )}
        </Box>

        {/* Right side - Status info and mobile username */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...(isMobile && { marginLeft: 'auto' })
        }}>
          {/* Mobile username (on right side) */}
          {userName && isMobile && (
            <Typography sx={{
              color: '#ffffff',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap'
            }}>
              Welcome, {userName}
            </Typography>
          )}

          {/* Status information - Desktop only */}
          {!isMobile && (
            <Box sx={{
              minWidth: '250px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <Typography color="#ffffff">
                {formatLastUpdate().label}
                <Box component="span" sx={{ display: 'inline-block', ml: 1, mt: .6 }}>
                  {formatLastUpdate().timestamp}
                </Box>
              </Typography>
              <Tooltip title={updatesPaused ? 'Resume Updates' : 'Pause Updates'}>
                <IconButton onClick={handlePauseToggle} sx={{ color: '#bbbbbb', ml: 1 , mt: .6}}>
                  {updatesPaused ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={getStatusTooltip()}>
                <Box
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor:
                      connectionStatus === 'Connected' ? '#00ff00' :
                      connectionStatus === 'Paused' ? '#ff4f4f' : '#ffff00',
                    ml: 1,
                    mt: .6
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <Sidebar />
      <div className="main-content" style={{
        marginTop: `${Math.min(Math.max(connectionPanelHeight || 48, 40), 120)}px`
      }}>
        {error && (
          <Box sx={{ p: 2, backgroundColor: '#ff4f4f', color: '#ffffff' }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/evtool" />} />

          {/* Modified Routes - EVTool no longer wrapped in ProtectedPage */}
          <Route path="/evtool" element={
            <EVTool
              bets={betsData}
              updatesPaused={updatesPaused}
              userName={userName}
              trackBet={trackBet}
              untrackBet={untrackBet}
              trackedBets={trackedBets}
              isAuthenticated={isAuthenticated}
            />
          } />

          {/* Protected Routes */}
          <Route path="/bettracker" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <BetTracker bets={betsData} updatesPaused={updatesPaused} userName={userName} trackBet={trackBet} untrackBet={untrackBet} trackedBets={trackedBets} setTrackedBets={setTrackedBets} ws={wsRef.current} closingOdds={closingOdds} />
            </ProtectedPage>
          } />
          <Route path="/pickem" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Pickem bets={betsData} updatesPaused={updatesPaused} userName={userName} />
            </ProtectedPage>
          } />
          <Route path="/arbitrage" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Arbitrage
                arbitrages={arbitrageData}
                updatesPaused={updatesPaused}
                userName={userName}
                trackBet={trackBet}
                untrackBet={untrackBet}
                trackedBets={trackedBets}
                isAuthenticated={isAuthenticated}
              />
            </ProtectedPage>
          } />
          <Route path="/results" element={
            <Results userName={userName} bettingMetrics={bettingMetrics} />
          } />
          <Route path="/middles" element={
            <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
              <Middles
                middles={middleData}
                updatesPaused={updatesPaused}
                userName={userName}
                trackBet={trackBet}
                untrackBet={untrackBet}
                trackedBets={trackedBets}
                isAuthenticated={isAuthenticated}
              />
            </ProtectedPage>
          } />

          {/* Public Routes (no authentication required) */}
          <Route path="/odds" element={<Odds bets={betsData} updatesPaused={updatesPaused} userName={userName} />} />
          <Route path="/props" element={<Props bets={betsData} updatesPaused={updatesPaused} userName={userName} />} />
          <Route path="/projections" element={
                        <ProtectedPage isAuthenticated={isAuthenticated} authError={authError} authReason={authReason}>
            <Projections 
              projections={nflProjections} 
              nbaProjections={nbaProjections}
              userName={userName} 
              isAuthenticated={isAuthenticated}
            />
                        </ProtectedPage>
          } />
          <Route path="/calculators" element={<Calculators userName={userName} />} />         
          <Route path="/changelog" element={<ChangeLog userName={userName} />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;